import { inject, injectable } from "tsyringe";

import { DocumentDetail } from "src/containers/documents-download/models/document-detail.model";
import { Document } from "src/containers/documents-download/models/document.model";
import { DocumentsDownloadService } from "src/containers/documents-download/services/document-download/documents-download.service";
import { HouseholdService } from "src/containers/household/services/household/household-service";
import {
    PolicyDetailActionsTab,
    PolicyDetailActionsTabType,
} from "src/containers/shared/policy-detail-ui/tab-actions/tab-actions";
import { hasOverdueCoupons } from "src/containers/payments/libs/has-overdue-coupons/has-overdue-coupons";
import { Building } from "src/shared/models/building.model";
import { Coupon } from "src/shared/models/payment-coupon.model";
import { PaymentSummary } from "src/shared/models/payment-summary.model";
import { Voucher } from "src/shared/models/voucher.model";
import { VoucherDownloadService } from "src/containers/documents-download/services/voucher-download/voucher-download-service";
import { autoBind } from "src/shared/decorators/autobind";
import { PgaPolicy } from "src/shared/models";

export interface DownloadPolicyDocumentParams {
    policyId: number;
    fileId: string;
    filename: string;
    stream: string;
    userIdentity: string;
    onSuccess?: VoidFunction;
    onFail?: VoidFunction;
}

/**
 * @description A ViewModel handles all the logic necessary
 * to make UI works. For example, it provides
 * the data to render the UI, do some biz logic,
 * etc. You can see this as an intermediate
 * between a Service and a view (UI).
 */
@injectable()
export class HouseholdViewModel {
    constructor(
        @inject("HouseholdService") private householdService: HouseholdService,
        @inject("VoucherDownloadService")
        private voucherService: VoucherDownloadService,
        @inject("DocumentsDownloadService")
        private documentsDownloadService: DocumentsDownloadService
    ) {}

    @autoBind
    canPay(): boolean {
        return true;
    }

    @autoBind
    canDownload(): boolean {
        return true;
    }

    @autoBind
    getActionTabs(
        displayPayments = false,
        summary: PaymentSummary | undefined
    ): PolicyDetailActionsTab[] {
        const tabs: PolicyDetailActionsTab[] = [
            {
                type: PolicyDetailActionsTabType.Coverages,
                showWarning: false,
            },
        ];
        if (!summary) {
            return tabs;
        }
        if (displayPayments) {
            const showWarning = hasOverdueCoupons(summary).hasOverdued;
            tabs.push({
                type: PolicyDetailActionsTabType.Payments,
                showWarning,
            });
        }
        return tabs;
    }

    @autoBind
    downloadVoucher(
        policy: PgaPolicy,
        coupon: Coupon,
        userIdentity = window.userIdentity
    ): Promise<Voucher> {
        return this.voucherService.download(policy, coupon, userIdentity);
    }

    @autoBind
    getPolicyDocument(
        policy: PgaPolicy,
        userIdentity = window.userIdentity
    ): Promise<Document | undefined> {
        return this.documentsDownloadService
            .getPgaDocumentList(policy, userIdentity)
            .then((documents) =>
                documents.find((d) => d.documentType === "Póliza")
            );
    }

    @autoBind
    getPolicyDocumentDetail(
        policy: PgaPolicy,
        doc: Document,
        userIdentity = window.userIdentity
    ): Promise<DocumentDetail> {
        return this.documentsDownloadService.getPgaDocumentDetail(
            policy,
            doc,
            userIdentity
        );
    }

    @autoBind
    getBuildings(
        userIdentity: string,
        policyId: number
    ): Promise<Array<Building>> {
        return this.householdService
            .getBuildings(userIdentity, policyId)
            .toPromise();
    }

    @autoBind
    getTotalPaid(coupons: Coupon[]): number {
        const paid = this.getPaidCoupons(coupons);
        return paid.reduce((prev, cur) => cur.amount + prev, 0);
    }

    @autoBind
    getPaidCoupons(coupons: Coupon[]): Coupon[] {
        return coupons.filter((coupon) => coupon.status === "PAGADO");
    }

    @autoBind
    getPendingCoupons(coupons: Coupon[]): Coupon[] {
        return coupons.filter((coupon) => coupon.status === "PENDIENTE");
    }

    @autoBind
    getHistoryCoupons(coupons: Coupon[]): Coupon[] {
        return coupons.filter((coupon) => coupon.status !== "PENDIENTE");
    }
}
